import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';
import { Spacing } from '../../styles';
import { Colors } from '../../themes';
import { SlideAction, ViewMode } from './enums';
import { SlideLayoutContextType, SlideLayoutProps } from './interfaces';

const SlideLayoutContext = createContext<SlideLayoutContextType | null>(null);

export const useSlideLayout = () => {
  const context = useContext(SlideLayoutContext);
  if (!context) {
    throw new Error('useSlideLayout must be used within a SlideLayout');
  }
  return context;
};

export const SlideLayout: React.FC<SlideLayoutProps> = ({
  columns,
  onPairChange,
  viewMode = ViewMode.DUAL,
  animationDuration = 500,
  onItemClick,
  onViewModeChange,
}) => {
  const HEADER_HEIGHT = 65;
  const { t } = useTranslation();
  const [currentStartIndex, setCurrentStartIndex] = useState(0); // Track which column is the starting position
  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(viewMode);
  const [sectionHeight, setSectionHeight] = useState<number>(600);

  const setContainerHeight = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setSectionHeight(height - HEADER_HEIGHT);
  }, []);
  
  // State for passing data between columns
  const [columnDataStore, setColumnDataStore] = useState<Record<string, unknown>>({});
  
  // State for persistent dynamic column titles
  const [columnTitles, setColumnTitles] = useState<Record<string, string | undefined>>({});

  // Get current visible columns based on view mode and start index
  const visibleColumns = useMemo(() => {
    if (currentViewMode === ViewMode.SINGLE) {
      return {
        left: columns[currentStartIndex] || null,
        right: null
      };
    } else {
      return {
        left: columns[currentStartIndex] || null,
        right: columns[currentStartIndex + 1] || null
      };
    }
  }, [columns, currentStartIndex, currentViewMode]);
  
  // Animation values for smooth transitions
  const leftColumnTranslateX = useSharedValue(0);
  const rightColumnTranslateX = useSharedValue(0);
  const leftColumnOpacity = useSharedValue(1);
  const rightColumnOpacity = useSharedValue(1);
  const leftColumnWidth = useSharedValue(currentViewMode === ViewMode.SINGLE ? 100 : 50);
  const rightColumnWidth = useSharedValue(currentViewMode === ViewMode.DUAL ? 50 : 0);
  
  const toggleViewMode = useCallback((mode?: ViewMode) => {
    if (!mode) {
      mode = currentViewMode === ViewMode.SINGLE ? ViewMode.DUAL : ViewMode.SINGLE;
    }

    setCurrentViewMode(mode);

    if (mode === ViewMode.SINGLE) {
      leftColumnWidth.value = withTiming(100, { duration: animationDuration });
      rightColumnWidth.value = withTiming(0, { duration: animationDuration });
      rightColumnOpacity.value = withTiming(0, { duration: animationDuration });
    } else {
      leftColumnWidth.value = withTiming(50, { duration: animationDuration });
      rightColumnWidth.value = withTiming(50, { duration: animationDuration });
      const nextColumn = columns[currentStartIndex + 1];

      if (nextColumn) {
        setTimeout(() => {
          rightColumnOpacity.value = withTiming(1, { duration: animationDuration });
        }, animationDuration / 2);
      }
    }
    onViewModeChange?.(mode);
    leftColumnTranslateX.value = 0;
    rightColumnTranslateX.value = 0;
    leftColumnOpacity.value = 1;
    rightColumnOpacity.value = mode === ViewMode.DUAL && visibleColumns.right ? 1 : 0;
  }, [
    onViewModeChange, 
    leftColumnTranslateX, 
    rightColumnTranslateX, 
    leftColumnOpacity, 
    rightColumnOpacity, 
    visibleColumns.right, 
    currentViewMode, 
    leftColumnWidth, 
    animationDuration, 
    rightColumnWidth, 
    columns, 
    currentStartIndex
  ]);
  
  const slideToNextColumn = () => {
    if (currentViewMode === ViewMode.SINGLE && currentStartIndex >= columns.length - 1) return;
    if (currentViewMode === ViewMode.DUAL && currentStartIndex >= columns.length - 2) return;
    
    if (currentViewMode === ViewMode.SINGLE) {
      // Single mode: slide current column out left, slide next column in from right
      leftColumnTranslateX.value = withTiming(-100, { duration: animationDuration / 2 });
      leftColumnOpacity.value = withTiming(0, { duration: animationDuration / 2 });
      
      setTimeout(() => {
        setCurrentStartIndex(prev => prev + 1);
        onPairChange?.(currentStartIndex + 1);
        
        // Reset position and slide in new column from right
        leftColumnTranslateX.value = 100;
        leftColumnTranslateX.value = withTiming(0, { duration: animationDuration / 2 });
        leftColumnOpacity.value = withTiming(1, { duration: animationDuration / 2 });
      }, animationDuration / 2);
    } else {
      // Double mode: left slides out, right slides to left position, new column fades in from right
      leftColumnTranslateX.value = withTiming(-100, { duration: animationDuration / 2 });
      leftColumnOpacity.value = withTiming(0, { duration: animationDuration / 2 });
      
      // Move right column to left position (shift 50% left)
      rightColumnTranslateX.value = withTiming(-100, { duration: animationDuration / 2 });
      
      setTimeout(() => {
        setCurrentStartIndex(prev => prev + 1);
        onPairChange?.(Math.floor((currentStartIndex + 1) / 2));
        
        // Reset positions: right column is now at left position, new column fades in at right
        leftColumnTranslateX.value = 0;
        rightColumnTranslateX.value = 0; // Reset right column position
        
        leftColumnOpacity.value = withTiming(1, { duration: animationDuration / 2 });
        
        // Fade in new right column if it exists
        const newRightIndex = currentStartIndex + 1;
        const newRightColumn = columns[newRightIndex];
        rightColumnOpacity.value = 0; // Start invisible
        rightColumnOpacity.value = withTiming(newRightColumn ? 1 : 0, { duration: animationDuration / 2 });
      }, animationDuration / 3);
    }
  };

  const slideToPreviousColumn = () => {
    if (currentStartIndex <= 0) return; // Can't go before first column
    
    if (currentViewMode === ViewMode.SINGLE) {
      // Single mode: slide current column out right, slide previous column in from left
      leftColumnTranslateX.value = withTiming(100, { duration: animationDuration / 2 });
      leftColumnOpacity.value = withTiming(0, { duration: animationDuration / 2 });
      
      setTimeout(() => {
        setCurrentStartIndex(prev => prev - 1);
        onPairChange?.(currentStartIndex - 1);
        
        // Reset position and slide in previous column from left
        leftColumnTranslateX.value = -100;
        leftColumnTranslateX.value = withTiming(0, { duration: animationDuration / 2 });
        leftColumnOpacity.value = withTiming(1, { duration: animationDuration / 2 });
      }, 100);
    } else {
      // Double mode: right fades out, left moves to right position, new column fades in from left
      rightColumnOpacity.value = withTiming(0, { duration: animationDuration / 2 });
      
      // Move left column to right position (shift 50% right)
      leftColumnTranslateX.value = withTiming(100, { duration: animationDuration / 2 });
      
      setTimeout(() => {
        setCurrentStartIndex(prev => prev - 1);
        onPairChange?.(Math.floor((currentStartIndex - 1) / 2));
        
        // Reset positions: left column is now at right position, new column fades in at left
        rightColumnTranslateX.value = 0; // Reset right column position
        leftColumnTranslateX.value = 0; // Reset left column position
        
        rightColumnOpacity.value = withTiming(1, { duration: animationDuration / 2 });
        
        // Fade in new left column
        const newLeftIndex = currentStartIndex - 1;
        const newLeftColumn = columns[newLeftIndex];
        leftColumnOpacity.value = 0; // Start invisible
        leftColumnOpacity.value = withTiming(newLeftColumn ? 1 : 0, { duration: animationDuration / 2 });
      }, animationDuration / 3);
    }
  };

  const navigateToColumn = (columnIndex: number) => {
    if (columnIndex < 0 || columnIndex >= columns.length) return;
    
    const currentIndex = currentStartIndex;
    if (columnIndex > currentIndex) {
      // Navigate forward
      const steps = columnIndex - currentIndex;
      let currentStep = 0;
      const stepForward = () => {
        if (currentStep < steps) {
          slideToNextColumn();
          currentStep++;
          setTimeout(stepForward, animationDuration);
        }
      };
      stepForward();
    } else if (columnIndex < currentIndex) {
      // Navigate backward
      const steps = currentIndex - columnIndex;
      let currentStep = 0;
      const stepBackward = () => {
        if (currentStep < steps) {
          slideToPreviousColumn();
          currentStep++;
          setTimeout(stepBackward, animationDuration);
        }
      };
      stepBackward();
    }
  };

  const handleItemClick = (slideAction: SlideAction, columnId?: string, itemData?: unknown) => {
    columnId = columnId || '';

    // Find the column index
    const currentColumnIndex = columns.findIndex(col => col.id === columnId);
    
    // Store itemData for the next column
    if (itemData) {
      const nextColumnId = columns[currentColumnIndex + 1]?.id;

      if (nextColumnId) {
        setColumnDataStore(prev => ({
          ...prev,
          [nextColumnId]: itemData
        }));
      }
    }
    
    // Call the external callback if provided
    onItemClick?.(slideAction, columnId, itemData);
    
    // Navigate based on the item clicked
    if (slideAction === SlideAction.NEXT) {
      const nextColumnIndex = currentColumnIndex + 1;
      // Navigate to next column if it exists
      if (nextColumnIndex < columns.length) {
        slideToNextColumn();
      }
    } else if (slideAction === SlideAction.PREV) {
      // Navigate to previous column if it exists
      if (currentColumnIndex > 0) {
        slideToPreviousColumn();
      }
    }
  };

  const updateColumnData = (data: unknown) => {
    const nextColumnId = visibleColumns.right?.id;

    if (nextColumnId) {
      setColumnDataStore(prev => ({
        ...prev,
        [nextColumnId]: data
      }));
    }
  };

  const updateViewMode = (mode?: ViewMode) => {
    if (!mode) {
      mode = currentViewMode === ViewMode.SINGLE ? ViewMode.DUAL : ViewMode.SINGLE;
    }

    toggleViewMode(mode);
  }

  // Callback to set a column's title by id
  const setColumnTitle = useCallback((columnId: string, title: string | undefined) => {
    const translatedTitle = title ? t(title) : undefined;
    setColumnTitles(prev => ({ ...prev, [columnId]: translatedTitle }));
  }, [t]);

  const columnStyle = useMemo(() => {
    return {
      ...styles.sectionContent,
      height: sectionHeight
    };
  }, [sectionHeight]);

  const contextValue: SlideLayoutContextType = {
    onSlideColumnChange: handleItemClick,
    currentColumnId: visibleColumns.left?.id || '',
    navigateToColumn,
    columnData: columnDataStore,
    setColumnData: updateColumnData,
    switchViewMode: updateViewMode,
    setColumnTitle,
  };

  const leftColumnAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: `${leftColumnTranslateX.value}%` }],
    opacity: leftColumnOpacity.value,
    width: `${leftColumnWidth.value}%`,
  }));

  const rightColumnAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: `${rightColumnTranslateX.value}%` }],
    opacity: rightColumnOpacity.value,
    width: `${rightColumnWidth.value}%`,
  }));

  return (
    <SlideLayoutContext.Provider value={contextValue}>
      <View style={styles.container}>
        <View style={styles.columnsContainer}>
          {/* Left Column */}
          {visibleColumns.left && (
            <Animated.View style={[
              styles.leftColumn,
              leftColumnAnimatedStyle
            ]}
            >
              <View style={styles.columnContent} onLayout={setContainerHeight}>
                <View style={styles.header}>
                  <Text style={styles.headerTitle}>
                    {columnTitles[visibleColumns.left?.id ?? ''] ?? t(visibleColumns.left.title)}
                  </Text>
                  {visibleColumns.left.headerComponent && (
                    <View style={styles.headerActions}>
                      {visibleColumns.left.headerComponent}
                    </View>
                  )}
                </View>
                <View
                  style={columnStyle}
                >
                  <ScrollView style={styles.scrollContainer}>
                    {visibleColumns.left.mainComponent}
                  </ScrollView>
                </View>
              </View>
            </Animated.View>
          )}

          {/* Right Column */}
          {currentViewMode === ViewMode.DUAL && (
            <Animated.View style={[
              rightColumnAnimatedStyle
            ]}>
              {visibleColumns.right && (
                <View style={styles.columnContent}>
                  <View style={styles.header}>
                    <Text style={styles.headerTitle}>
                      {columnTitles[visibleColumns.right?.id ?? ''] ?? t(visibleColumns.right.title)}
                    </Text>
                    {visibleColumns.right.headerComponent && (
                      <View style={styles.headerActions}>
                        {visibleColumns.right.headerComponent}
                      </View>
                    )}
                  </View>
                  <View
                    style={columnStyle}
                  >
                    <ScrollView style={styles.scrollContainer}>
                      {visibleColumns.right.mainComponent}
                    </ScrollView>
                  </View>
                </View>
              )}
            </Animated.View>
          )}
        </View>
      </View>
    </SlideLayoutContext.Provider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  columnsContainer: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  leftColumn: {
    paddingRight: 16,
  },
  scrollContainer: {
    flex: 1,
  },
  columnContent: {
    flex: 1,
  },
  header: {
    borderRadius: 4,
    marginBottom: 4,
    backgroundColor: Colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.black,
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
  },
  sectionContent: {
    padding: Spacing.basePadding.base,
    borderRadius: 4,
    backgroundColor: Colors.white,
    flexGrow: 1,
  },
});